/**
 * Pure rendering functions for innernet inject formats.
 * Works from in-memory data (Supabase rows) — no file I/O.
 */

export const INJECT_FORMATS = [
  'claude', 'gpt', 'cursor', 'brief', 'tokens', 'context-md', 'json',
] as const

export type InjectFormat = (typeof INJECT_FORMATS)[number]

export interface ProjectRow {
  slug: string
  name: string
  type: string
  tagline: string | null
  schema_version: string
  manifest: Record<string, unknown>
}

export interface DimensionRow {
  name: string
  content: string
  lines: number
}

export interface NodeRow {
  node_id: string
  type: string
  content: Record<string, unknown>
}

export interface CommitRow {
  hash: string
  parent_hash: string | null
  summary: string
  trigger: string | null
  committed_at: string
}

// ── helpers ──────────────────────────────────────────────────────────────────

function nodeOneLiner(n: NodeRow): string {
  const fields = ['role', 'description', 'status', 'tagline', 'summary', 'context']
  for (const f of fields) {
    const v = n.content[f]
    if (typeof v === 'string' && v.length > 0) {
      return v.length > 90 ? v.slice(0, 87) + '…' : v
    }
  }
  return n.type
}

function groupByType(nodes: NodeRow[]): Map<string, NodeRow[]> {
  const m = new Map<string, NodeRow[]>()
  for (const n of nodes) {
    if (!m.has(n.type)) m.set(n.type, [])
    m.get(n.type)!.push(n)
  }
  for (const list of m.values()) list.sort((a, b) => a.node_id.localeCompare(b.node_id))
  return m
}

function learningOrder(
  dims: DimensionRow[],
  manifest: Record<string, unknown>,
): DimensionRow[] {
  const seq = manifest.learning_sequence as Array<{ ref: string; essential?: boolean }> | undefined
  if (!seq?.length) return [...dims].sort((a, b) => a.name.localeCompare(b.name))

  const byName = new Map(dims.map(d => [d.name, d]))
  const ordered: DimensionRow[] = []
  const seen = new Set<string>()

  for (const step of seq) {
    const name = step.ref.replace(/.*\//, '').replace(/\.md$/, '')
    const d = byName.get(name)
    if (d) { ordered.push(d); seen.add(name) }
  }
  for (const d of dims) if (!seen.has(d.name)) ordered.push(d)
  return ordered
}

function head(project: ProjectRow, commits: CommitRow[]): string {
  const m = project.manifest as { commits?: { head?: string } }
  return m.commits?.head ?? (commits[0]?.hash ?? '—')
}

function hierarchy(project: ProjectRow) {
  const m = project.manifest as {
    hierarchy?: { parent?: string; children?: string[]; siblings?: string[]; tags?: string[] }
  }
  return m.hierarchy ?? { parent: null, children: [], siblings: [], tags: [] }
}

// ── renderers ────────────────────────────────────────────────────────────────

function renderClaude(
  project: ProjectRow,
  dims: DimensionRow[],
  nodes: NodeRow[],
  commits: CommitRow[],
): string {
  const lines: string[] = []
  const hier = hierarchy(project)
  const ordered = learningOrder(dims, project.manifest)

  lines.push(`# ${project.name}`, '')
  if (project.tagline) lines.push(`> ${project.tagline}`, '')

  lines.push('## AI Context — read in this order', '')
  ordered.forEach((d, i) => lines.push(`${i + 1}. **${d.name}**`))
  lines.push('')

  lines.push('## Project metadata', '')
  lines.push(`- **Type:** ${project.type}`)
  const created = (project.manifest.created as string | undefined) ?? '—'
  lines.push(`- **Created:** ${created}`)
  lines.push(`- **Head:** \`${head(project, commits)}\` (${commits.length} commits)`)
  if (hier.parent) lines.push(`- **Parent:** ${hier.parent}`)
  if (hier.children?.length) lines.push(`- **Children:** ${hier.children.join(', ')}`)
  if (hier.siblings?.length) lines.push(`- **Siblings:** ${hier.siblings.join(', ')}`)
  if (hier.tags?.length) lines.push(`- **Tags:** ${hier.tags.join(' · ')}`)
  lines.push('')

  lines.push('## Dimensions', '')
  for (const d of ordered) lines.push(d.content.trim(), '')

  lines.push('## Nodes', '')
  for (const [type, list] of [...groupByType(nodes)].sort()) {
    lines.push(`### ${type}`, '')
    for (const n of list) lines.push(`- **${n.node_id}** — ${nodeOneLiner(n)}`)
    lines.push('')
  }

  if (commits.length) {
    lines.push('## Recent commits', '')
    for (const c of commits.slice(0, 5)) {
      lines.push(`- \`${c.hash.slice(0, 7)}\` ${c.committed_at.slice(0, 10)} — ${c.summary}`)
    }
    lines.push('')
  }

  lines.push('---', '')
  lines.push(`_Generated from innernet. Source: \`innernet.live/projects/${project.slug}\` — the live memory every connected AI reads. This file is a snapshot; the memory is the source of truth._`)
  return lines.join('\n') + '\n'
}

function renderGpt(project: ProjectRow, dims: DimensionRow[], nodes: NodeRow[]): string {
  const lines: string[] = []
  lines.push(`Project: ${project.name} (${project.type})`)
  if (project.tagline) lines.push(`Mission: ${project.tagline}`)
  lines.push('')

  lines.push('Context:')
  for (const d of dims.slice(0, 3)) {
    lines.push('', `### ${d.name}`, d.content.trim())
  }

  lines.push('', 'Key entities:')
  for (const [type, list] of [...groupByType(nodes)].sort()) {
    for (const n of list) lines.push(`- ${n.node_id} (${type}): ${nodeOneLiner(n)}`)
  }
  return lines.join('\n') + '\n'
}

function renderCursor(project: ProjectRow, dims: DimensionRow[], nodes: NodeRow[]): string {
  const lines: string[] = []
  lines.push(`# Project: ${project.name}`)
  if (project.tagline) lines.push(`# ${project.tagline}`)
  lines.push('')

  lines.push('## Always remember')
  for (const d of dims.slice(0, 3)) {
    const firstPara = d.content.split('\n\n')[0].replace(/^#+\s+\w+\s*\n+/, '').trim()
    lines.push(`- **${d.name}:** ${firstPara.replace(/\n/g, ' ').slice(0, 200)}`)
  }
  lines.push('')

  lines.push('## Generated')
  lines.push(`- innernet cloud — project ${project.slug}`)
  lines.push(`- live at: innernet.live/projects/${project.slug} — connected AI tools read it there`)
  return lines.join('\n') + '\n'
}

function renderBrief(project: ProjectRow, dims: DimensionRow[], nodes: NodeRow[]): string {
  const lines: string[] = []
  lines.push(`# ${project.name} — brief`, '')
  if (project.tagline) lines.push(`*${project.tagline}*`, '')

  const narrativeDims = ['vision', 'intent', 'audience', 'aesthetic', 'positioning', 'voice', 'mission', 'product', 'market', 'differentiation']
  for (const name of narrativeDims) {
    const d = dims.find(x => x.name === name)
    if (d) lines.push(d.content.trim(), '')
  }

  lines.push('## People & assets', '')
  for (const [type, list] of [...groupByType(nodes)].sort()) {
    if (type === 'concept' || type === 'decision') continue
    lines.push(`### ${type}`, '')
    for (const n of list) lines.push(`- **${n.node_id}** — ${nodeOneLiner(n)}`)
    lines.push('')
  }

  lines.push('---', '')
  lines.push(`_Brief generated from innernet cloud — ${project.slug}_`)
  return lines.join('\n') + '\n'
}

function renderContextMd(
  project: ProjectRow,
  dims: DimensionRow[],
  nodes: NodeRow[],
  commits: CommitRow[],
): string {
  const lines: string[] = []
  lines.push(`# CONTEXT — ${project.name}`, '')
  if (project.tagline) lines.push(`> ${project.tagline}`, '')

  const h = head(project, commits)
  const created = (project.manifest.created as string | undefined) ?? '—'
  lines.push(`Type: ${project.type} · Created: ${created} · Head: \`${h}\` · ${commits.length} commits`, '')

  for (const d of [...dims].sort((a, b) => a.name.localeCompare(b.name))) {
    lines.push(d.content.trim(), '')
  }

  lines.push('## Nodes', '')
  for (const [type, list] of [...groupByType(nodes)].sort()) {
    lines.push(`### ${type}`, '')
    for (const n of list) lines.push(`- **${n.node_id}**: ${nodeOneLiner(n)}`)
    lines.push('')
  }

  if (commits.length) {
    lines.push('## History', '')
    for (const c of commits.slice(0, 10)) {
      lines.push(`- \`${c.hash.slice(0, 7)}\` ${c.committed_at.slice(0, 10)} — ${c.summary}`)
    }
  }
  return lines.join('\n') + '\n'
}

function renderJson(project: ProjectRow, dims: DimensionRow[], nodes: NodeRow[]): string {
  return JSON.stringify({ project, dimensions: dims, nodes }, null, 2) + '\n'
}

// ── main entry ───────────────────────────────────────────────────────────────

export function renderInjectCloud(
  format: InjectFormat,
  project: ProjectRow,
  dims: DimensionRow[],
  nodes: NodeRow[],
  commits: CommitRow[],
): string {
  switch (format) {
    case 'claude':      return renderClaude(project, dims, nodes, commits)
    case 'gpt':         return renderGpt(project, dims, nodes)
    case 'cursor':      return renderCursor(project, dims, nodes)
    case 'brief':       return renderBrief(project, dims, nodes)
    case 'tokens':      return renderGpt(project, dims, nodes) // fallback for now
    case 'context-md':  return renderContextMd(project, dims, nodes, commits)
    case 'json':        return renderJson(project, dims, nodes)
    default:            throw new Error(`Unknown format: ${format}`)
  }
}
